import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, Settings, Bell, CheckCircle2 } from 'lucide-react';
import { fetchFormSettings, saveFormSettings } from '@/services/formassemblyService';
import { FormSettings } from '@/types/formassembly';
import { useToast } from '@/hooks/use-toast';

interface FormSettingsEditorProps {
  formIdExternal: string;
}

export const FormSettingsEditor: React.FC<FormSettingsEditorProps> = ({ formIdExternal }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form Availability state
  const [formStatus, setFormStatus] = useState<'active' | 'archived'>('active');
  const [allowResponsesFrom, setAllowResponsesFrom] = useState<'anyone' | 'authenticated_users' | 'specific_users'>('anyone');
  const [displayFrom, setDisplayFrom] = useState('');
  const [displayUntil, setDisplayUntil] = useState('');
  const [responseQuota, setResponseQuota] = useState('');
  const [unavailableMessage, setUnavailableMessage] = useState('');

  // Processing Settings state
  const [anonymizeIp, setAnonymizeIp] = useState(false);
  const [allowReviewBeforeSubmit, setAllowReviewBeforeSubmit] = useState(false);
  const [allowSaveResume, setAllowSaveResume] = useState(false);
  const [useRecaptcha, setUseRecaptcha] = useState(false);
  const [enableEsignature, setEnableEsignature] = useState(false);

  // E-Signature expanded settings state
  const [esignatureContactName, setEsignatureContactName] = useState('');
  const [esignatureContactEmail, setEsignatureContactEmail] = useState('');
  const [esignatureShowAsContact, setEsignatureShowAsContact] = useState(false);
  const [esignatureCollectInitials, setEsignatureCollectInitials] = useState(false);
  const [esignatureSigningMessage, setEsignatureSigningMessage] = useState('');

  // Acknowledgment Settings state
  const [acknowledgmentMessage, setAcknowledgmentMessage] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');

  // Email Collection state
  const [emailCollectionField, setEmailCollectionField] = useState('');

  // Your Notifications state
  const [yourNotificationEnabled, setYourNotificationEnabled] = useState<'disabled' | 'enabled_text' | 'enabled_html'>('disabled');
  const [yourNotificationTemplate, setYourNotificationTemplate] = useState<'default' | 'customized'>('default');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [yourNotificationBcc, setYourNotificationBcc] = useState('');
  const [yourNotificationSenderEmail, setYourNotificationSenderEmail] = useState('');
  const [yourNotificationSenderName, setYourNotificationSenderName] = useState('');
  const [notificationSubject, setNotificationSubject] = useState('');
  const [notificationContent, setNotificationContent] = useState('');
  const [yourNotificationAttachPdf, setYourNotificationAttachPdf] = useState(false);

  // Auto-Responder state
  const [autoResponderEnabled, setAutoResponderEnabled] = useState<'disabled' | 'enabled_text' | 'enabled_html'>('disabled');

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Fetch existing settings
  const { data: settings, isLoading, error } = useQuery<FormSettings | null, Error>({
    queryKey: ['formSettings', formIdExternal],
    queryFn: () => fetchFormSettings(formIdExternal),
    enabled: !!formIdExternal,
  });

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (updatedSettings: Partial<Omit<FormSettings, 'id' | 'user_id' | 'form_id_external' | 'created_at' | 'updated_at'>>) =>
      saveFormSettings(formIdExternal, updatedSettings),
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['formSettings', formIdExternal] });

      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    },
    onError: (error) => {
      setSaveStatus('error');
      toast({
        variant: 'destructive',
        title: 'Save Failed',
        description: error.message || 'Could not save form settings.',
      });

      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    },
  });

  // Helper to format datetime for input
  const formatDateTimeForInput = (isoString: string | null): string => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      // Format as YYYY-MM-DDTHH:MM for datetime-local input
      return date.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  };

  // Initialize form fields when settings are loaded
  useEffect(() => {
    if (settings) {
      // Form availability settings
      setFormStatus(settings.form_status || 'active');
      setAllowResponsesFrom(settings.allow_responses_from || 'anyone');
      setDisplayFrom(formatDateTimeForInput(settings.display_from));
      setDisplayUntil(formatDateTimeForInput(settings.display_until));
      setResponseQuota(settings.response_quota?.toString() || '');
      setUnavailableMessage(settings.unavailable_message || '');

      // Processing settings
      setAnonymizeIp(settings.anonymize_ip);
      setAllowReviewBeforeSubmit(settings.allow_review_before_submit);
      setAllowSaveResume(settings.allow_save_resume);
      setUseRecaptcha(settings.use_recaptcha);
      setEnableEsignature(settings.enable_esignature);

      // E-Signature expanded settings
      setEsignatureContactName(settings.esignature_contact_name || '');
      setEsignatureContactEmail(settings.esignature_contact_email || '');
      setEsignatureShowAsContact(settings.esignature_show_as_contact || false);
      setEsignatureCollectInitials(settings.esignature_collect_initials || false);
      setEsignatureSigningMessage(settings.esignature_signing_message || '');

      // Acknowledgment settings
      setAcknowledgmentMessage(settings.acknowledgment_message || '');
      setRedirectUrl(settings.redirect_url || '');

      // Email collection
      setEmailCollectionField(settings.email_collection_field || '');

      // Your notifications
      setYourNotificationEnabled(settings.your_notification_enabled || 'disabled');
      setYourNotificationTemplate(settings.your_notification_template || 'default');
      setNotificationEmail(settings.notification_email || '');
      setYourNotificationBcc(settings.your_notification_bcc || '');
      setYourNotificationSenderEmail(settings.your_notification_sender_email || '');
      setYourNotificationSenderName(settings.your_notification_sender_name || '');
      setNotificationSubject(settings.notification_subject || '');
      setNotificationContent(settings.notification_content || '');
      setYourNotificationAttachPdf(settings.your_notification_attach_pdf || false);

      // Auto-responder
      setAutoResponderEnabled(settings.auto_responder_enabled || 'disabled');
    }
  }, [settings]);

  // Immediate save for checkboxes and selects
  const handleImmediateChange = (field: string, value: boolean | string) => {
    setSaveStatus('saving');
    saveMutation.mutate({ [field]: value });
  };

  // Debounced save for text fields
  const handleTextChange = (field: string, value: string) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      setSaveStatus('saving');
      saveMutation.mutate({ [field]: value || null });
    }, 1000);

    setDebounceTimer(timer);
  };

  // Debounced save for number fields
  const handleNumberChange = (field: string, value: string) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      setSaveStatus('saving');
      const numValue = value ? parseInt(value, 10) : null;
      saveMutation.mutate({ [field]: numValue });
    }, 1000);

    setDebounceTimer(timer);
  };

  // Debounced save for datetime fields
  const handleDateTimeChange = (field: string, value: string) => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    const timer = setTimeout(() => {
      setSaveStatus('saving');
      // Convert datetime-local value to ISO string or null
      const isoValue = value ? new Date(value).toISOString() : null;
      saveMutation.mutate({ [field]: isoValue });
    }, 1000);

    setDebounceTimer(timer);
  };

  // Save status indicator component
  const SaveStatusIndicator = () => (
    <>
      {saveStatus === 'saving' && (
        <div className="flex items-center text-sm text-muted-foreground">
          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          Saving...
        </div>
      )}
      {saveStatus === 'saved' && (
        <div className="flex items-center text-sm text-green-600">
          <CheckCircle2 className="mr-1 h-4 w-4" />
          Saved
        </div>
      )}
    </>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-2 text-muted-foreground">Loading form settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mb-6">
        <AlertDescription>
          Error loading form settings: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {/* PROCESSING Section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Settings className="mr-2 h-5 w-5" />
              <CardTitle>Processing Settings</CardTitle>
            </div>
            <SaveStatusIndicator />
          </div>
          <CardDescription className="space-y-2">
            <span>FormAssembly doesn't expose processing settings programmatically available, so we can't back them up automatically. To ensure a complete backup, copy these settings from your form on FormAssembly.com.</span>
            <span className="block text-xs border-l-2 border-amber-500 pl-2 mt-2">
              <strong>NOTE:</strong> When restoring a form, you'll need to manually re-apply these settings to your restored form on FormAssembly.com. You'll also need to re-enable any connectors, and workflows. It's some work, but far easier than recreating the entire form from scratch.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* FORM AVAILABILITY Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Form Availability</h3>

            <div className="space-y-2">
              <Label>Status:</Label>
              <RadioGroup
                value={formStatus}
                onValueChange={(value: 'active' | 'archived') => {
                  setFormStatus(value);
                  handleImmediateChange('form_status', value);
                }}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="active" id="status-active" />
                  <Label htmlFor="status-active" className="font-normal cursor-pointer">Active</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="archived" id="status-archived" />
                  <Label htmlFor="status-archived" className="font-normal cursor-pointer">Archived <span className="text-muted-foreground">(no processing)</span></Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="allow-responses-from">Allow Responses From:</Label>
              <Select
                value={allowResponsesFrom}
                onValueChange={(value: 'anyone' | 'authenticated_users' | 'specific_users') => {
                  setAllowResponsesFrom(value);
                  handleImmediateChange('allow_responses_from', value);
                }}
              >
                <SelectTrigger id="allow-responses-from">
                  <SelectValue placeholder="Select who can respond" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="anyone">Anyone</SelectItem>
                  <SelectItem value="authenticated_users">Authenticated Users</SelectItem>
                  <SelectItem value="specific_users">Specific Users</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="display-from">Display From:</Label>
                <Input
                  id="display-from"
                  type="datetime-local"
                  value={displayFrom}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDisplayFrom(value);
                    handleDateTimeChange('display_from', value);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  The form will be enabled at the beginning of the day specified unless time is specified as well. Leave empty if no start date.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display-until">Display Until:</Label>
                <Input
                  id="display-until"
                  type="datetime-local"
                  value={displayUntil}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDisplayUntil(value);
                    handleDateTimeChange('display_until', value);
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  The form will be disabled at the beginning of the day specified unless time is specified as well. Leave empty if no end date.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="response-quota">Response Quota:</Label>
              <Input
                id="response-quota"
                type="number"
                min="0"
                placeholder=""
                value={responseQuota}
                onChange={(e) => {
                  const value = e.target.value;
                  setResponseQuota(value);
                  handleNumberChange('response_quota', value);
                }}
                className="max-w-[200px]"
              />
              <p className="text-xs text-muted-foreground">
                The form will become unavailable once this number of submissions is reached. Incomplete form submissions and form views do not count towards the quota. Leave empty if no quota.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unavailable-message">'Unavailable' Message:</Label>
              <Textarea
                id="unavailable-message"
                placeholder="This form is no longer accepting submissions."
                rows={4}
                value={unavailableMessage}
                onChange={(e) => {
                  const value = e.target.value;
                  setUnavailableMessage(value);
                  handleTextChange('unavailable_message', value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                This message will be displayed once the form is no longer available (archived or date/quota restriction in place).
              </p>
            </div>
          </div>

          {/* IP ANONYMIZATION Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">IP Anonymization</h3>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="anonymize-ip"
                checked={anonymizeIp}
                onCheckedChange={(checked) => {
                  const value = checked === true;
                  setAnonymizeIp(value);
                  handleImmediateChange('anonymize_ip', value);
                }}
              />
              <Label htmlFor="anonymize-ip" className="cursor-pointer font-normal">
                Anonymize respondent IP addresses for this form
              </Label>
            </div>
          </div>

          {/* OPTIONAL PROCESSING FEATURES Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Optional Processing Features</h3>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="allow-review"
                checked={allowReviewBeforeSubmit}
                onCheckedChange={(checked) => {
                  const value = checked === true;
                  setAllowReviewBeforeSubmit(value);
                  handleImmediateChange('allow_review_before_submit', value);
                }}
              />
              <Label htmlFor="allow-review" className="cursor-pointer font-normal">
                Allow respondents to review their response before a final submit
              </Label>
            </div>
          </div>

          {/* SAVE & RESUME Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Save & Resume</h3>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="allow-save-resume"
                checked={allowSaveResume}
                onCheckedChange={(checked) => {
                  const value = checked === true;
                  setAllowSaveResume(value);
                  handleImmediateChange('allow_save_resume', value);
                }}
              />
              <Label htmlFor="allow-save-resume" className="cursor-pointer font-normal">
                Allow respondents to save their progress and resume the form later
              </Label>
            </div>
          </div>

          {/* SPAM FILTER OPTIONS Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Spam Filter Options</h3>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="use-recaptcha"
                checked={useRecaptcha}
                onCheckedChange={(checked) => {
                  const value = checked === true;
                  setUseRecaptcha(value);
                  handleImmediateChange('use_recaptcha', value);
                }}
              />
              <Label htmlFor="use-recaptcha" className="cursor-pointer font-normal">
                Use Google reCAPTCHA (a challenge that helps prevent spam)
              </Label>
            </div>
          </div>

          {/* E-SIGNATURE Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">E-Signature</h3>
            <div className="flex items-start space-x-3">
              <Checkbox
                id="enable-esignature"
                checked={enableEsignature}
                onCheckedChange={(checked) => {
                  const value = checked === true;
                  setEnableEsignature(value);
                  handleImmediateChange('enable_esignature', value);
                }}
              />
              <Label htmlFor="enable-esignature" className="cursor-pointer font-normal">
                Enable E-Signature
              </Label>
            </div>

            {enableEsignature && (
              <div className="space-y-4 pl-6 border-l-2 border-muted ml-2">
                <p className="text-sm text-muted-foreground">
                  Please provide the contact information of a person able to answer any questions related to the signature process. This information will be shared with the respondents.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="esignature-contact-name">
                    Contact Name: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="esignature-contact-name"
                    type="text"
                    placeholder="Jane Smith"
                    value={esignatureContactName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEsignatureContactName(value);
                      handleTextChange('esignature_contact_name', value);
                    }}
                    className="max-w-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="esignature-contact-email">
                    Contact Email Address: <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="esignature-contact-email"
                    type="email"
                    placeholder="contact@example.com"
                    value={esignatureContactEmail}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEsignatureContactEmail(value);
                      handleTextChange('esignature_contact_email', value);
                    }}
                    className="max-w-sm"
                  />
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="esignature-show-as-contact"
                    checked={esignatureShowAsContact}
                    onCheckedChange={(checked) => {
                      const value = checked === true;
                      setEsignatureShowAsContact(value);
                      handleImmediateChange('esignature_show_as_contact', value);
                    }}
                  />
                  <Label htmlFor="esignature-show-as-contact" className="cursor-pointer font-normal">
                    Show E-Signature emails as being sent on behalf of this contact.
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="esignature-collect-initials"
                    checked={esignatureCollectInitials}
                    onCheckedChange={(checked) => {
                      const value = checked === true;
                      setEsignatureCollectInitials(value);
                      handleImmediateChange('esignature_collect_initials', value);
                    }}
                  />
                  <Label htmlFor="esignature-collect-initials" className="cursor-pointer font-normal">
                    Collect respondent's initials along with the signature.
                  </Label>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="esignature-signing-message">Signing Message:</Label>
                  <Textarea
                    id="esignature-signing-message"
                    placeholder="Example: The undersigned has the authority to agree to the information submitted and certifies that all information provided herein is true and correct."
                    rows={4}
                    value={esignatureSigningMessage}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEsignatureSigningMessage(value);
                      handleTextChange('esignature_signing_message', value);
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    This custom message will be displayed on the signature page.
                  </p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* NOTIFICATIONS Section */}
      <Card className="mb-8">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="mr-2 h-5 w-5" />
              <CardTitle>Notifications</CardTitle>
            </div>
            <SaveStatusIndicator />
          </div>
          <CardDescription className="space-y-2">
            <span>FormAssembly doesn't make notification settings programmatically available, so we can't back them up automatically. To ensure a complete backup, copy these settings from your form on FormAssembly.com.</span>
            <span className="block text-xs border-l-2 border-amber-500 pl-2 mt-2">
              <strong>NOTE:</strong> When restoring a form, you'll need to manually re-apply these settings to your restored form on FormAssembly.com. You'll also need to re-enable any connectors, and workflows. It's some work, but far easier than recreating the entire form from scratch.
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* ACKNOWLEDGMENT Section */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Acknowledgment</h3>

            <div className="space-y-2">
              <Label htmlFor="acknowledgment-message">Either show this "Thank You" message:</Label>
              <Textarea
                id="acknowledgment-message"
                placeholder="Thank you for your submission!"
                rows={4}
                value={acknowledgmentMessage}
                onChange={(e) => {
                  const value = e.target.value;
                  setAcknowledgmentMessage(value);
                  handleTextChange('acknowledgment_message', value);
                }}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="redirect-url">...or redirect to this page:</Label>
              <Input
                id="redirect-url"
                type="url"
                placeholder="https://"
                value={redirectUrl}
                onChange={(e) => {
                  const value = e.target.value;
                  setRedirectUrl(value);
                  handleTextChange('redirect_url', value);
                }}
              />
            </div>
          </div>

          {/* EMAIL COLLECTION Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Email Collection</h3>

            <p className="text-sm text-muted-foreground">
              Does your form ask for the email address of the respondent? If yes, please select the corresponding question.
            </p>

            <div className="space-y-2">
              <Label htmlFor="email-collection-field">Email Question:</Label>
              <Input
                id="email-collection-field"
                type="text"
                placeholder="Enter field name or alias (e.g., tfa_email)"
                value={emailCollectionField}
                onChange={(e) => {
                  const value = e.target.value;
                  setEmailCollectionField(value);
                  handleTextChange('email_collection_field', value);
                }}
              />
              <p className="text-xs text-muted-foreground">
                This will be the reply-to email address for your notifications
              </p>
            </div>
          </div>

          {/* YOUR NOTIFICATIONS Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Your Notifications</h3>

            <div className="space-y-2">
              <Label htmlFor="your-notification-enabled">Your Email Notification:</Label>
              <Select
                value={yourNotificationEnabled}
                onValueChange={(value: 'disabled' | 'enabled_text' | 'enabled_html') => {
                  setYourNotificationEnabled(value);
                  handleImmediateChange('your_notification_enabled', value);
                }}
              >
                <SelectTrigger id="your-notification-enabled">
                  <SelectValue placeholder="Select notification status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disabled">disabled</SelectItem>
                  <SelectItem value="enabled_text">enabled (text email)</SelectItem>
                  <SelectItem value="enabled_html">enabled (HTML email)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {yourNotificationEnabled !== 'disabled' && (
              <>
                <div className="space-y-2">
                  <Label>Email Template:</Label>
                  <RadioGroup
                    value={yourNotificationTemplate}
                    onValueChange={(value: 'default' | 'customized') => {
                      setYourNotificationTemplate(value);
                      handleImmediateChange('your_notification_template', value);
                    }}
                    className="flex flex-col space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="default" id="template-default" />
                      <Label htmlFor="template-default" className="font-normal cursor-pointer">Default template</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="customized" id="template-customized" />
                      <Label htmlFor="template-customized" className="font-normal cursor-pointer">Customized template</Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notification-email">Email Responses To:</Label>
                  <Input
                    id="notification-email"
                    type="email"
                    placeholder="notifications@example.com"
                    value={notificationEmail}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNotificationEmail(value);
                      handleTextChange('notification_email', value);
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notification-bcc">BCC:</Label>
                  <Input
                    id="notification-bcc"
                    type="email"
                    placeholder="bcc@example.com"
                    value={yourNotificationBcc}
                    onChange={(e) => {
                      const value = e.target.value;
                      setYourNotificationBcc(value);
                      handleTextChange('your_notification_bcc', value);
                    }}
                  />
                </div>

                {yourNotificationTemplate === 'customized' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="sender-email">Sender Email:</Label>
                      <Input
                        id="sender-email"
                        type="email"
                        placeholder="no-reply@formassembly.com"
                        value={yourNotificationSenderEmail}
                        onChange={(e) => {
                          const value = e.target.value;
                          setYourNotificationSenderEmail(value);
                          handleTextChange('your_notification_sender_email', value);
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sender-name">Sender Name:</Label>
                      <Input
                        id="sender-name"
                        type="text"
                        placeholder="FormAssembly Notification"
                        value={yourNotificationSenderName}
                        onChange={(e) => {
                          const value = e.target.value;
                          setYourNotificationSenderName(value);
                          handleTextChange('your_notification_sender_name', value);
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notification-subject">Email Subject:</Label>
                      <Input
                        id="notification-subject"
                        type="text"
                        placeholder='Your form "%%FORM_NAME%%" got a response'
                        value={notificationSubject}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotificationSubject(value);
                          handleTextChange('notification_subject', value);
                        }}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notification-content">Email Content:</Label>
                      <Textarea
                        id="notification-content"
                        placeholder={'Your form "%%FORM_NAME%%" has received the following response:\n\nSubmitted on: %%SUBMITTED_DATE%%\nCompletion time: %%COMPLETION_TIME%%\n\n%%RESPONSE%%\n\n%%FILE_LIST%%'}
                        rows={8}
                        value={notificationContent}
                        onChange={(e) => {
                          const value = e.target.value;
                          setNotificationContent(value);
                          handleTextChange('notification_content', value);
                        }}
                        className="font-mono text-sm"
                      />
                    </div>
                  </>
                )}

                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="attach-pdf"
                    checked={yourNotificationAttachPdf}
                    onCheckedChange={(checked) => {
                      const value = checked === true;
                      setYourNotificationAttachPdf(value);
                      handleImmediateChange('your_notification_attach_pdf', value);
                    }}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="attach-pdf" className="cursor-pointer font-normal">
                      Attach PDF of form response
                    </Label>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* AUTO-RESPONDER Section */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Auto-Responder</h3>

            <div className="space-y-2">
              <Label htmlFor="auto-responder-enabled">Auto-Responder:</Label>
              <Select
                value={autoResponderEnabled}
                onValueChange={(value: 'disabled' | 'enabled_text' | 'enabled_html') => {
                  setAutoResponderEnabled(value);
                  handleImmediateChange('auto_responder_enabled', value);
                }}
              >
                <SelectTrigger id="auto-responder-enabled">
                  <SelectValue placeholder="Select auto-responder status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="disabled">disabled</SelectItem>
                  <SelectItem value="enabled_text">enabled (text email)</SelectItem>
                  <SelectItem value="enabled_html">enabled (HTML email)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
